import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Image as ImageIcon, Film } from 'lucide-react';
import {
  listScheduleIntents,
  updateScheduleIntent,
  deleteScheduleIntent,
} from '../../api/schedulesApi';
import dashboardSocket from '../../sockets/dashboardSocket';
import { resolveSlotLabel, resolveSlotAsset } from '../../components/timeline/labelResolver';
import { normalizeIntent } from './timelineGeometry';
import { hashColor } from '../../components/timeline/color';
import ClipBlock from './ClipBlock';
import './timelineEditor.css';

const LANE_HEIGHT_PX = 48;
const DEBOUNCE_MS = 500;
const LEFT_RAIL_W = 180;

// Stable color derived from contentId, same palette as the collapsed
// TimelineTrack bars — so Rarity is always green (or whatever its hash
// picks), no matter the zoom level or collapsed-vs-expanded view. Border
// is a darker shade of the bg for subtle outline.
const darken = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.max(0, ((n >> 16) & 0xff) - 40);
  const g = Math.max(0, ((n >> 8) & 0xff) - 40);
  const b = Math.max(0, (n & 0xff) - 40);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};
const colorForContent = (key) => {
  const c = hashColor(key || '');
  return { bg: c.bg, border: darken(c.bg) };
};

/**
 * TimelineLanes — renders the expanded content lanes for a single screen as
 * SIBLING ROWS inside the outer ScreenTimeline grid. No wrapping card, no
 * duplicate ruler, no duplicate NOW line. Column widths match the outer
 * exactly so everything lines up pixel-perfect.
 *
 * Scheduling new content happens from the Media tab — this view edits
 * existing clips (drag to shift, resize handles, delete via inspector).
 *
 * Props:
 *  - screenId, projectId       required
 *  - windowRange {startMin,endMin}  from parent (outer ruler uses same)
 *  - viewportWidth             measured width of the outer viewport column
 *  - contentMap                Map indexed by contentId → {title,name,filename,mediaType,thumbnailUrl}
 *  - selectedClipId, setSelectedClipId  lifted to parent so inspector can find
 *                              the active clip
 */
export default function TimelineLanes({
  screenId,
  projectId,
  windowRange,
  viewportWidth,
  contentMap,
  selectedClipId,
  setSelectedClipId,
  onIntentsChange,
  isMultiDay = false,
  windowDays = 1,
  rangeAnchor = null,
  onDrillToDay = null,
}) {
  const [intents, setIntents] = useState([]);
  const debounceTimers = useRef(new Map());
  const pendingPatches = useRef(new Map());
  const selfOriginTokens = useRef(new Set());

  // ── Fetch intents for this screen ─────────────────────────────────
  const reload = useCallback(async () => {
    if (!screenId || !projectId) return;
    try {
      const list = await listScheduleIntents(projectId, screenId);
      const normalized = list.map(normalizeIntent).filter(Boolean);
      setIntents(normalized);
      onIntentsChange?.(normalized);
    } catch (err) {
      console.warn('TimelineLanes load:', err?.message);
    }
  }, [projectId, screenId, onIntentsChange]);

  useEffect(() => {
    reload();
  }, [reload]);

  // ── Socket live-collab ─────────────────────────────────────────────
  useEffect(() => {
    if (!screenId || !projectId) return undefined;
    dashboardSocket.connect();
    const handler = (payload) => {
      if (!payload) return;
      if (payload.screenId && payload.screenId !== screenId) return;
      if (payload.projectId && payload.projectId !== projectId) return;
      if (payload.intentId && selfOriginTokens.current.has(payload.intentId)) return;
      reload();
    };
    dashboardSocket.onIntentChanged(handler);
    return () => dashboardSocket.offIntentChanged(handler);
  }, [projectId, screenId, reload]);

  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      pendingPatches.current.clear();
    };
  }, []);

  // ── Patch helpers ──────────────────────────────────────────────────
  const patchLocal = useCallback((id, partial) => {
    setIntents((prev) => prev.map((it) => {
      if (it.id !== id) return it;
      const next = { ...it, ...partial };
      if ('startTime' in partial) {
        const parsed = parseHHMM(partial.startTime);
        if (parsed !== null) next.startMinutes = parsed;
      }
      if ('endTime' in partial) {
        const parsed = parseHHMM(partial.endTime);
        if (parsed !== null) next.endMinutes = parsed;
      }
      return next;
    }));
  }, []);

  const scheduleServerPatch = useCallback((id, partial) => {
    const prev = pendingPatches.current.get(id) || {};
    pendingPatches.current.set(id, { ...prev, ...partial });
    const existing = debounceTimers.current.get(id);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(async () => {
      const patch = pendingPatches.current.get(id);
      pendingPatches.current.delete(id);
      debounceTimers.current.delete(id);
      if (!patch || Object.keys(patch).length === 0) return;
      selfOriginTokens.current.add(id);
      setTimeout(() => selfOriginTokens.current.delete(id), 4000);
      try {
        const updated = await updateScheduleIntent(id, patch);
        const normalized = normalizeIntent(updated);
        if (normalized) {
          setIntents((prev2) => {
            const next = prev2.map((it) => (it.id === id ? normalized : it));
            onIntentsChange?.(next);
            return next;
          });
        }
      } catch (err) {
        console.warn('TimelineLanes patch err:', err?.message);
        reload();
      }
    }, DEBOUNCE_MS);
    debounceTimers.current.set(id, timer);
  }, [reload, onIntentsChange]);

  // ── Delete ─────────────────────────────────────────────────────────
  // Adding new content is intentionally NOT done here — users go to
  // the Media tab to pick + schedule with full folder/multi-select UI.
  const handleDelete = useCallback(
    async (id) => {
      if (!id) return;
      const snapshot = intents;
      setIntents((prev) => prev.filter((it) => it.id !== id));
      if (selectedClipId === id) setSelectedClipId(null);
      selfOriginTokens.current.add(id);
      setTimeout(() => selfOriginTokens.current.delete(id), 4000);
      try {
        await deleteScheduleIntent(id);
      } catch (err) {
        console.warn('TimelineLanes delete err:', err?.message);
        setIntents(snapshot);
      }
    },
    [intents, selectedClipId, setSelectedClipId],
  );

  // ── Group intents into lanes (one per distinct contentId) ──────────
  const lanes = useMemo(() => {
    const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
    const todayYmd = new Date().toISOString().slice(0, 10);

    const byContent = new Map();
    for (const it of intents) {
      // Skip intents whose time window has fully passed for today AND whose
      // date range has also ended — they will never play again.
      const endMin = it.endMinutes ?? 1439;
      const endDate = it.endDate ? String(it.endDate).slice(0, 10) : null;
      // In week/month view the intent recurs on future days — only suppress
      // the time-of-day check in day view.
      const expiredToday = !isMultiDay && endMin <= nowMin;
      const expiredDate = endDate && endDate < todayYmd;
      if (expiredDate || expiredToday) continue;

      // One lane per contentId — all contentIds in the intent get their own row.
      const contentIds = it.contentIds?.length > 0 ? it.contentIds : [`__clip:${it.id}`];
      for (const cid of contentIds) {
        if (!byContent.has(cid)) byContent.set(cid, []);
        byContent.get(cid).push(it);
      }
    }
    const out = [];
    for (const [key, laneIntents] of byContent.entries()) {
      const isVirtual = key.startsWith('__clip:');
      const sampleSlot = { contentId: isVirtual ? null : key };
      const { label: resolvedName, resolved } = resolveSlotLabel(sampleSlot, contentMap);
      const asset = resolveSlotAsset(sampleSlot, contentMap);
      const earliestStart = laneIntents.reduce(
        (min, c) => (c.startMinutes < min ? c.startMinutes : min),
        Number.POSITIVE_INFINITY,
      );
      // Prefer the library name when it was actually resolved; otherwise fall
      // back to the intent name so contents not yet in the library (or whose
      // contentId was stored as the intent name) still show human-readable text.
      const label = resolved ? resolvedName : (laneIntents[0]?.name || resolvedName || '(untitled)');
      out.push({
        key,
        contentId: isVirtual ? null : key,
        label,
        mediaType: asset?.mediaType || null,
        thumbnailUrl: asset?.thumbnailUrl || null,
        intents: laneIntents,
        color: colorForContent(key),
        earliestStart,
      });
    }
    out.sort((a, b) => {
      if (a.earliestStart !== b.earliestStart) return a.earliestStart - b.earliestStart;
      return String(a.label).localeCompare(String(b.label));
    });
    return out;
  }, [contentMap, intents]);

  // Expose delete handler upstream so the inspector can call it.
  // We stash on window.__timelineLanesDelete for the inspector to look up;
  // cleaner to pass via context but this keeps the wiring surface tight.
  useEffect(() => {
    const handlers = window.__timelineLanesHandlers || (window.__timelineLanesHandlers = {});
    handlers[screenId] = {
      delete: handleDelete,
      patchLocal,
      scheduleServerPatch,
    };
    return () => {
      if (handlers[screenId]) delete handlers[screenId];
    };
  }, [handleDelete, patchLocal, scheduleServerPatch, screenId]);

  return (
    <>
      {lanes.map((lane, laneIdx) => (
        <LaneRow
          key={lane.key}
          lane={lane}
          laneIdx={laneIdx}
          windowRange={windowRange}
          viewportWidth={viewportWidth}
          selectedClipId={selectedClipId}
          onSelectClip={setSelectedClipId}
          onLocalPatch={patchLocal}
          onCommit={scheduleServerPatch}
          isMultiDay={isMultiDay}
          windowDays={windowDays}
          rangeAnchor={rangeAnchor}
          onDrillToDay={onDrillToDay}
        />
      ))}
    </>
  );
}

/* ── LaneRow: [content label 180px | track viewport 1fr] ──────────────
   Day: renders one ClipBlock per intent (hour-level drag/shorten).
   Week/Month: renders N virtual ClipBlocks per intent (one per day in
   [startDate, endDate] ∩ window), positioned via dayOffsetMinutes so the
   clip lands at the right day column AT its time-of-day. Drag on any
   pulse updates the underlying intent's startTime/endTime — recurring
   intent = all sibling pulses shift together. Answers "what's playing
   at 11 PM this Friday" truthfully. */
function LaneRow({
  lane,
  laneIdx,
  windowRange,
  viewportWidth,
  selectedClipId,
  onSelectClip,
  onLocalPatch,
  onCommit,
  isMultiDay,
  windowDays,
  rangeAnchor,
  onDrillToDay,
}) {
  const trackRef = useRef(null);

  const anchorDate = useMemo(() => {
    if (!isMultiDay || !rangeAnchor) return null;
    const d = new Date(`${rangeAnchor}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [isMultiDay, rangeAnchor]);

  const activeDayIdxs = (intent) => {
    if (!isMultiDay || !anchorDate) return [0];
    const dayIdxFromAnchor = (iso) => {
      const d = new Date(`${iso}T00:00:00`);
      if (Number.isNaN(d.getTime())) return 0;
      return Math.round((d - anchorDate) / 86400000);
    };
    const sIso = intent.startDate ? String(intent.startDate).slice(0, 10) : null;
    const eIso = intent.endDate ? String(intent.endDate).slice(0, 10) : null;
    const fromIdx = sIso ? Math.max(0, dayIdxFromAnchor(sIso)) : 0;
    const toIdx = eIso
      ? Math.min(windowDays - 1, dayIdxFromAnchor(eIso))
      : windowDays - 1;
    if (toIdx < fromIdx) return [];
    const out = [];
    for (let i = fromIdx; i <= toIdx; i++) out.push(i);
    return out;
  };

  return (
    <div className="timeline-lane-row">
      <div className="timeline-lane-row__label">
        <LaneIcon mediaType={lane.mediaType} thumbnailUrl={lane.thumbnailUrl} label={lane.label} />
        <span className="timeline-lane-row__label-text" title={lane.label}>
          {lane.label}
        </span>
      </div>
      <div ref={trackRef} className="timeline-lane-row__track">
        {lane.intents.flatMap((it) => {
          const days = isMultiDay ? activeDayIdxs(it) : [0];
          return days.map((dayIdx) => (
            <ClipBlock
              key={`${it.id}:d${dayIdx}`}
              intent={it}
              laneIndex={laneIdx}
              laneHeightPx={LANE_HEIGHT_PX}
              windowRange={windowRange}
              containerWidth={viewportWidth}
              containerRef={trackRef}
              color={lane.color}
              displayName={lane.label}
              selected={it.id === selectedClipId}
              onSelect={onSelectClip}
              onLocalPatch={onLocalPatch}
              onCommit={onCommit}
              dayOffsetMinutes={dayIdx * 1440}
              onDoubleClickDrill={
                isMultiDay && onDrillToDay && anchorDate
                  ? () => {
                      const target = new Date(anchorDate);
                      target.setDate(target.getDate() + dayIdx);
                      const y = target.getFullYear();
                      const mo = String(target.getMonth() + 1).padStart(2, '0');
                      const da = String(target.getDate()).padStart(2, '0');
                      onDrillToDay(`${y}-${mo}-${da}`);
                    }
                  : null
              }
            />
          ));
        })}
      </div>
    </div>
  );
}

function LaneIcon({ mediaType, thumbnailUrl, label }) {
  const [hover, setHover] = useState(false);
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const ref = useRef(null);
  const tooltipRef = useRef(null);
  const mt = String(mediaType || '').toLowerCase();
  const isVideo = mt.includes('video');
  const isImageish = !mt || mt.startsWith('image');
  const canShowImg = !!thumbnailUrl && isImageish;
  // For videos, thumbnailUrl currently holds the raw .mp4 URL (backend
  // doesn't extract poster frames yet — see project_sumadhura_cms_routes
  // notes). Use it as the <video src> directly so the tooltip plays the
  // actual content silently on hover.
  const canPlayVideo = !!thumbnailUrl && isVideo;

  const onEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setAnchor({ x: rect.right + 8, y: rect.top });
    }
    setHover(true);
  };
  const onLeave = () => setHover(false);

  // Auto-flip tooltip when it would overflow viewport edges. Mirrors the
  // SlotTooltip behavior so hovering rows near bottom/right doesn't clip.
  useLayoutEffect(() => {
    if (!hover || !tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    const W = window.innerWidth;
    const H = window.innerHeight;
    const PAD = 8;
    let left = anchor.x;
    let top = anchor.y;
    if (left + rect.width > W - PAD) left = Math.max(PAD, anchor.x - rect.width - 16);
    if (top + rect.height > H - PAD) top = Math.max(PAD, H - rect.height - PAD);
    setPos({ left, top });
  }, [hover, anchor]);

  let body;
  if (thumbnailUrl) {
    body = <img src={thumbnailUrl} alt="" className="timeline-lane-row__thumb" />;
  } else if (isVideo) {
    body = <Film size={12} aria-hidden="true" />;
  } else {
    body = <ImageIcon size={12} aria-hidden="true" />;
  }

  return (
    <span
      ref={ref}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
    >
      {body}
      {hover && (canShowImg || canPlayVideo || isVideo) && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            left: `${pos.left}px`,
            top: `${pos.top}px`,
            zIndex: 1000,
            pointerEvents: 'none',
            backgroundColor: 'var(--gray-900, #1c1917)',
            padding: 0,
            borderRadius: '10px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
            maxWidth: 'min(640px, 92vw)',
            minWidth: '480px',
          }}
        >
          <div style={{
            backgroundColor: '#000',
            maxHeight: 'min(480px, 60vh)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            color: 'rgba(255,255,255,0.55)',
            fontSize: '40px',
          }}>
            {canPlayVideo ? (
              <video
                src={thumbnailUrl}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                style={{
                  width: '100%',
                  maxHeight: 'min(480px, 60vh)',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            ) : canShowImg ? (
              <img
                src={thumbnailUrl}
                alt={label || ''}
                loading="lazy"
                decoding="async"
                onError={(e) => { e.currentTarget.parentElement.textContent = isVideo ? '▶' : '⊠'; }}
                style={{
                  width: '100%',
                  maxHeight: 'min(480px, 60vh)',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            ) : (isVideo ? '▶' : '⊠')}
          </div>
          {label && (
            <div style={{
              padding: '8px 12px',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {label}
            </div>
          )}
        </div>
      )}
    </span>
  );
}

function parseHHMM(value) {
  if (typeof value !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}
